import { FC, use } from "react";
import { Content, isFilled } from "@prismicio/client";
import { PrismicRichText, SliceComponentProps } from "@prismicio/react";
import { appLocales, getPrismicLocale, Locales } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { PrismicNextLink } from "@prismicio/next";
import InspirationItem from "@/components/atoms/Inspiration/item/InspirationItem";
import { getAllByIDs, getByID } from "@/lib/prismic";
import { __, getPermalink } from "@/lib/i18n/functions";
import { mergeFrenchImage } from "@/helpers/frenchImage";

export type InspirationCategroyGridProps = SliceComponentProps<Content.InspirationCategroyGridSlice> & {
  context?: { locale?: Locales };
};

const InspirationCategroyGrid: FC<InspirationCategroyGridProps> = ({ slice, context }) => {
  const locale = context?.locale || appLocales.defaultLocale;
  const frLocale = getPrismicLocale("fr");

  const { title, description, link_label, link, categories } = slice.primary;

  const filledCategories = categories.map((item) => item.category).filter(isFilled.contentRelationship);

  const categoriesList = use(
    getAllByIDs(
      filledCategories.map((p) => p.id),
      { lang: getPrismicLocale(locale) }
    )
  ) as unknown as Content.CategorieDocument[];

  const categoriesFr = use(
    Promise.all(
      categoriesList.map(async (doc) => {
        const fr = doc.alternate_languages?.find((alt) => alt.lang === frLocale);
        return fr ? await getByID(fr.id, { lang: frLocale }) : null;
      })
    )
  ) as unknown as Content.CategorieDocument[];

  const mergedCategories = mergeFrenchImage(categoriesList, categoriesFr, getPrismicLocale(locale), frLocale);

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      aria-labelledby={__("Product inspirations", locale)}
      className={cn("container flex flex-col items-center gap-7.5 px-4 py-10 max-sm:hidden md:px-7.5 xl:flex-row")}
    >
      <header className="text-dark-primary flex w-full max-w-[39.625rem] flex-1 shrink flex-col items-center justify-center gap-4.5">
        {isFilled.richText(title) && (
          <div id="products-inspirations" className="font-cabinet-grotesk text-center text-3xl font-medium">
            <PrismicRichText field={title} />
          </div>
        )}
        {isFilled.richText(description) && (
          <div className="text-primary-light max-w-80 text-center text-sm leading-[115%]">
            <PrismicRichText field={description} />
          </div>
        )}
        {link && (
          <PrismicNextLink className="text-sm font-medium underline underline-offset-2" field={link}>
            {link_label}
          </PrismicNextLink>
        )}
      </header>
      {mergedCategories && mergedCategories.length >= 1 && (
        <ul className="flex flex-wrap items-center justify-center gap-2.5 lg:flex-nowrap">
          {mergedCategories.map((item, index) => (
            <li key={index}>
              <InspirationItem
                className="max-w-full min-[690px]:max-w-[17.813rem]"
                buttonClassname="bg-dark-primary/80 h-10"
                imageURl={item.data.featuredImage.url || ""}
                image={item.data.featuredImage || null}
                buttonText={item.data.title || ""}
                buttonSize="sm"
                buttonVariant="slider"
                href={`${getPermalink(locale, "inspiration_archive")}/${item.uid}`}
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default InspirationCategroyGrid;
